import { useState, useCallback } from "react";
import {
  usePaginatedResource,
  PaginatedFetcher,
  pageFrom,
} from "./usePaginatedResource";
import { useEntityOperations } from "./useEntityOperations";
import { useEntity } from "./useEntity";
import { useEntitySearch, SearchPage } from "./useEntitySearch";
import { RecipeService } from "../services/recipeService";
import {
  Recipe,
  CreateRecipeInput,
  UpdateRecipeInput,
  RecipeFilters,
} from "../types/recipe";

export const useRecipes = (
  initialPage: number = 1,
  initialLimit: number = 15
) => {
  const fetcher = useCallback<PaginatedFetcher<Recipe, RecipeFilters>>(
    async (page, filters) => {
      const response = await RecipeService.getRecipes(page, filters);
      if (!response) return null;

      // recipeService returns the raw paginator keys rather than the
      // {items,total,page,limit} the other services use.
      return pageFrom(response.data, {
        total: response.total,
        page: response.current_page,
        limit: response.per_page,
      });
    },
    []
  );

  const { items, ...rest } = usePaginatedResource<Recipe, RecipeFilters>(
    fetcher,
    {
      initialPage,
      initialLimit,
      errorMessage: "Failed to fetch recipes",
    }
  );

  return { recipes: items, ...rest };
};

export const useRecipeOperations = () => {
  const { run, runBoolean, ...rest } = useEntityOperations();

  const createAdminRecipe = useCallback(
    (recipeData: CreateRecipeInput) =>
      run(
        () => RecipeService.createAdminRecipe(recipeData),
        "Failed to create recipe"
      ),
    [run]
  );

  const updateRecipe = useCallback(
    (recipeId: number, updates: UpdateRecipeInput) =>
      run(
        () => RecipeService.updateRecipe(recipeId, updates),
        "Failed to update recipe"
      ),
    [run]
  );

  const deleteRecipe = useCallback(
    (recipeId: number) =>
      runBoolean(
        () => RecipeService.deleteRecipe(recipeId),
        "Failed to delete recipe"
      ),
    [runBoolean]
  );

  return { createAdminRecipe, updateRecipe, deleteRecipe, ...rest };
};

export const useRecipe = (recipeId: number | null) => {
  const fetcher = useCallback(
    (id: number) => RecipeService.getRecipeById(id),
    []
  );

  const { data, setData, ...rest } = useEntity<Recipe>(recipeId, fetcher, {
    notFoundMessage: "Recipe not found",
    errorMessage: "An unexpected error occurred while fetching recipe",
  });

  return { recipe: data, setRecipe: setData, ...rest };
};

export const useRecipeSearch = () => {
  const fetcher = useCallback(
    async (
      searchTerm: string,
      page: number
    ): Promise<SearchPage<Recipe> | null> => {
      const response = await RecipeService.searchRecipes(searchTerm, page);
      if (!response.success || !response.data) return null;

      return {
        items: response.data.data,
        total: response.data.total,
        page: response.data.current_page,
        lastPage: response.data.last_page,
      };
    },
    []
  );

  const { search, ...rest } = useEntitySearch<Recipe>(fetcher, {
    limit: 15,
    errorMessage: "Failed to search recipes",
  });

  return { search, searchRecipes: search, ...rest };
};
